import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Phone, Clock, Send, Loader2, Instagram, ArrowUpRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Name, email and message are required.");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error("Enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. We'll reply soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (e2) {
      toast.error("Could not send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="scroll-mt-20 py-20 lg:py-28 bg-[#F8FAFC] border-t border-[#E2E8F0]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#0055FF]">
              Contact
            </div>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0A192F] tracking-tight">
              Visit us in Jaipur. Or send us a note.
            </h2>
            <p className="mt-4 text-[#475569] leading-relaxed max-w-lg">
              Reach out for consultation availability, insurance queries, or
              simply to learn more about how we work.
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoCard icon={MapPin} title="Visit">
                Kanwal Plaza, Ground Floor,
                <br />
                Opp. DMart, Kardhani,
                <br />
                Kalwar Road, Jaipur 302012
              </InfoCard>
              <InfoCard icon={Phone} title="Call">
                +91 92518 00011
                <br />
                +91 92140 99331
              </InfoCard>
              <InfoCard icon={Mail} title="Email">
                shunyacdc@gmail.com
              </InfoCard>
              <InfoCard icon={Clock} title="Hours">
                Mon–Sun: 8:00 AM – 8:00 PM
              </InfoCard>
              <InfoCard
                icon={Instagram}
                title="Instagram"
                href="https://www.instagram.com/shunyacdc/"
                testid="instagram-card"
              >
                @shunyacdc
              </InfoCard>
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="bg-white border border-[#E2E8F0] rounded-2xl p-6 sm:p-8 shadow-sm"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label>Name *</Label>
                <Input
                  data-testid="contact-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Email *</Label>
                <Input
                  data-testid="contact-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@example.com"
                />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label>Phone</Label>
                <Input
                  data-testid="contact-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+91 98xxxxxxxx"
                />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label>Message *</Label>
                <Textarea
                  data-testid="contact-message"
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="How can we help?"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-btn"
              className="mt-6 inline-flex items-center gap-2 bg-[#0055FF] text-white hover:bg-[#0044CC] disabled:opacity-70 rounded-full px-6 py-3 font-medium transition-colors"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  Send Message
                  <Send className="h-4 w-4" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function InfoCard({ icon: Icon, title, children, href, testid }) {
  const content = (
    <>
      <div className="flex items-center justify-between">
        <div className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-[#E6F5EE] text-[#0055FF]">
          <Icon className="h-4 w-4" />
        </div>
        {href && (
          <ArrowUpRight className="h-4 w-4 text-[#94A3B8] group-hover:text-[#0055FF] transition-colors" />
        )}
      </div>
      <div className="mt-3 text-xs font-bold uppercase tracking-widest text-[#475569]">
        {title}
      </div>
      <div className="mt-1 text-sm text-[#0F172A] leading-relaxed">{children}</div>
    </>
  );

  const commonClasses =
    "group bg-white border border-[#E2E8F0] rounded-xl p-5 transition-all";

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testid || `info-card-${title.toLowerCase()}`}
        className={`${commonClasses} block hover:border-[#0055FF] hover:-translate-y-0.5`}
      >
        {content}
      </a>
    );
  }

  return (
    <div
      data-testid={testid || `info-card-${title.toLowerCase()}`}
      className={commonClasses}
    >
      {content}
    </div>
  );
}
